import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import accounting from 'accounting';
import ExternalLinkIcon from 'components/v2/icons/ExternalLinkIcon';
import Pagination from 'components/v2/Pagination';
import formatMoney from 'utils/formatters/Money';
import EditButton from '../../../v2/buttons/EditButton';
import ExclamationIcon from '../../../v2/icons/ExclamationIcon';
import Tooltip from '../../../v2/Tooltip';
import SessionStore from '../../../../stores/SessionStore';
import { LocaleContext } from 'providers/locale-settings-context';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';

const MARGIN_THRESHOLD = 15;

function renderExclamationIcon() {
  return <ExclamationIcon className="w-5 h-5 text-red-700" />;
}

function Link({ children, url }) {
  return (
    <a
      data-testid="a-217aef46"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
    >
      <div data-testid="div-ed4eed3c" className="flex items-center space-x-1">
        <span data-testid="span-376212ac" className="flex-1">
          {children}
        </span>
        <ExternalLinkIcon className="w-4 h-4 flex-shrink-0" />
      </div>
    </a>
  );
}
function renderEditButton(item, i, handleEditButtonClick) {
  return function () {
    return (
      <EditButton
        dataTestId="EditButton-3533181c"
        disabled={!SessionStore.isAbleToAdjustCarrierBulkCost()}
        onClick={handleEditButtonClick(item, i)}
      />
    );
  };
}

export default function List({
  items,
  perPage,
  page,
  pages,
  total,
  onPageClick,
  onEdit,
  onViewShipments,
}) {
  const { t } = useTranslation();
  const { formatDate } = useContext(LocaleContext);

  function handlePageClick(selectedPage) {
    onPageClick(selectedPage);
  }

  function handleEditButtonClick(item, index) {
    return function () {
      onEdit(item, index);
    };
  }

  function handleViewShipments(item, index) {
    return function (e) {
      e.preventDefault();
      onViewShipments(item, index);
    };
  }

  function renderMargin(item) {
    const tooltipId = `margin-tooltip-${item.id}`;
    if (typeof item.margin === 'undefined' || item.margin === null) {
      return (
        <Tooltip
          dataTestId="Tooltip-5a1a6947"
          id={tooltipId}
          body={renderExclamationIcon}
        >
          {t('BELOW_MARGIN_THRESHOLD', {
            threshold: MARGIN_THRESHOLD,
          })}
        </Tooltip>
      );
    }

    const belowThreshold = item.margin < MARGIN_THRESHOLD;

    return (
      <div
        data-testid="div-8d8c1e6f"
        className={classnames('flex items-center space-x-1', {
          'text-red-800': belowThreshold,
        })}
      >
        {belowThreshold && (
          <Tooltip
            dataTestId="Tooltip-9587dcfb"
            id={tooltipId}
            body={renderExclamationIcon}
          >
            {t('BELOW_MARGIN_THRESHOLD', {
              threshold: MARGIN_THRESHOLD,
            })}
          </Tooltip>
        )}
        <span data-testid="span-e6d83a8b">
          {accounting.formatNumber(item.margin)}%
        </span>
      </div>
    );
  }
  const ccbatEnabled = featureEnabled(CONFIGURED_FEATURES.CCBAT);
  return (
    <div data-testid="div-4ebab118" className="overflow-x-auto">
      <table data-testid="table-fd070433" className="default-table">
        <thead data-testid="thead-77555c5a">
          <tr data-testid="tr-1cd869de">
            <th data-testid="th-ab8b3d9c">{t('SHIPPER')}</th>
            <th data-testid="th-e0d80364">{t('ORIGIN')}</th>
            <th data-testid="th-2fde5d1a">{t('DESTINATION')}</th>
            <th data-testid="th-7bc8e8c6">{t('CARRIER')}</th>
            <th data-testid="th-321f5376">{t('SHIPMENT_COUNT')}</th>
            <th data-testid="th-91f74cff">{t('SHIPPER_RATE')}</th>
            <th data-testid="th-9f67b1ab">{t('CARRIER_RATE')}</th>
            {ccbatEnabled && (
              <>
                <th data-testid="th-fc86c001">{t('CARRIER_FSC')}</th>
                <th data-testid="th-1ab49a0c">{t('TOTAL')}</th>
                <th data-testid="th-eefd3072">{t('APPROVAL_DUE_DATE')}</th>
              </>
            )}
            <th data-testid="th-05e34584">{t('MARGIN')}</th>
            <th data-testid="th-ee3d40b7">{t('ACTIONS')}</th>
          </tr>
        </thead>
        <tbody data-testid="tbody-7aa6d674">
          {items.map((item, i) => {
            return (
              <tr data-testid={`tr-445a7749-${i}`} key={item.id}>
                <td data-testid={`td-c829514b-${i}`}>
                  <Link url={`/users/${item.shipper?._id}`}>
                    {item.shipper?.shipper_name}
                  </Link>
                </td>
                <td data-testid={`td-a3e71a61-${i}`}>{item.originAddress}</td>
                <td data-testid={`td-838d8711-${i}`}>
                  {item.destinationAddress}
                </td>
                <td data-testid={`td-475c9720-${i}`}>
                  <Link url={`/users/${item.carrier?._id}`}>
                    {item.carrier?.carrier_name}
                  </Link>
                </td>
                <td data-testid={`td-336fe74b-${i}`}>
                  <a
                    data-testid={`a-9fe5e00c-${i}`}
                    href="#"
                    onClick={handleViewShipments(item, i)}
                  >
                    {item.count}
                  </a>
                </td>
                <td data-testid={`td-df96f793-${i}`}>
                  {formatMoney(item.shipper_base_cost, item?.currency)}
                </td>
                <td data-testid={`td-7ff54a96-${i}`}>
                  {formatMoney(item.carrier_base_cost, item?.currency)}
                </td>
                {ccbatEnabled && (
                  <>
                    <td
                      data-testid={`td-8cdfe81e-${i}`}
                      className="text-center"
                    >
                      {item?.carrierFscTotal
                        ? formatMoney(item?.carrierFscTotal, item?.currency)
                        : '-'}
                    </td>
                    <td
                      data-testid={`td-d7be4174-${i}`}
                      className="text-center"
                    >
                      {item?.carrierFscTotal
                        ? formatMoney(
                            item.carrierFscTotal + item.carrier_base_cost,
                            item?.currency,
                          )
                        : formatMoney(item.carrier_base_cost, item?.currency)}
                    </td>
                    <td
                      data-testid={`td-dd94fd1c-${i}`}
                      className="text-center"
                    >
                      {item?.approvalDueDate
                        ? formatDate(item?.approvalDueDate)
                        : '-'}
                    </td>
                  </>
                )}
                <td data-testid={`td-43a8a4f4-${i}`}>{renderMargin(item)}</td>
                <td data-testid={`td-0a82f048-${i}`}>
                  <div
                    data-testid={`div-8f778537-${i}`}
                    className="flex space-x-2 items-center"
                  >
                    <Tooltip
                      dataTestId={`Tooltip-034c5b38-${i}`}
                      id={`edit-tooltip-${item.id}`}
                      position="top"
                      body={renderEditButton(item, i, handleEditButtonClick)}
                    >
                      {t('EDIT_RATE')}
                    </Tooltip>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {total > 0 && (
        <Pagination
          dataTestId="Pagination-ff8a63ff"
          className=""
          perPage={perPage}
          page={page}
          pages={pages}
          total={total}
          onPageClick={handlePageClick}
        />
      )}
    </div>
  );
}
